import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const toStringSearchValues = (searchParams: Record<string, string | number | string[] | number[] | null>) => {
  const result: Record<string, string> = {};
  Object.entries(searchParams).forEach(([key, value]) => {
    if (value === null) return;

    if (Array.isArray(value)) {
      result[key] = value.join(',');
    } else {
      result[key] = String(value);
    }
  });
  return result;
};

const getSearchParams = (href: string) => {
  try {
    const urlSearchParams = new URL(href).searchParams || {};
    return Object.fromEntries(urlSearchParams.entries());
  } catch {
    return {};
  }
}

const useSearchParams = () => {
  const history = useHistory();
  const { pathname } = useLocation();

  const keepRef = useRef<{ searchParams: Record<string, string> }>({ searchParams: {} });
  const [windowHref, setWindowHref] = useState(window.location.href);

  keepRef.current.searchParams = useMemo(() => {
    return getSearchParams(windowHref);
  }, [windowHref]);

  const updateSearchParams = useCallback(
    (newSearchParams: Record<string, string | number | string[] | number[] | null>, options?: { replace: boolean }) => {
      let search = new URLSearchParams(
        toStringSearchValues({ ...keepRef.current.searchParams, ...newSearchParams })
      ).toString();
      search = search.replace(/%2C/g, ',');

      if (options?.replace) {
        history.replace({ pathname, search });
      } else {
        history.push({ pathname, search });
      }
    },
    [pathname, history]
  );

  const setSearchParams = useCallback(
    (newSearchParams: Record<string, string | number | string[] | number[] | null>, options?: { replace: boolean }) => {
      let search = new URLSearchParams(toStringSearchValues(newSearchParams)).toString();
      search = search.replace(/%2C/g, ',');

      if (options?.replace) {
        history.replace({ pathname, search });
      } else {
        history.push({ pathname, search });
      }
    },
    [pathname, history]
  );

  // trigger when "href" change
  const newWindowHref = window.location.href;
  useEffect(() => {
    setWindowHref(newWindowHref);
  }, [newWindowHref]);

  return {
    searchParams: keepRef.current.searchParams,
    updateSearchParams,
    setSearchParams,
  };
};

export default useSearchParams;
